import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Input, Button, Tooltip, Modal } from 'antd';
import moment from 'moment';
import { request } from '~/_shared/instance';

import DocumentUpload from './Drawers/DocumentUpload';
import { DocumentsTable } from './Tables/DocumentsTable';
import {
  BottonsContainer,
  ButtonsWrapper,
  DocumentButtonWrapper,
  Footer,
  StyledDrawer,
  SwitchContainer,
  SwitchLabel,
  SwtichStyled
} from './styles';
import { AddDocumentButton, RequiredDocumentErrorMessage } from '../styles';
import { DeliveryMethodButtons } from './DeliveryMethodButtons';

const CustomFields = ({ data, task, form, users, updateCustomers, onClose }) => {
  const [documentDrawer, setDocumentDrawer] = useState({
    isOpen: false,
    data: null
  });
  const [files, setFiles] = useState([]);
  const [isWithoutMovement, setIsWithoutMovement] = useState(false);
  const [hasRequiredDocumentError, setHasRequiredDocumentError] = useState(false);
  const [hasNotDocument, setHasNotDocument] = useState(false);
  const [isSubmiting, setIsSubmiting] = useState(false);

  useEffect(() => {
    if (!data) return;
    
    setIsWithoutMovement(data.is_without_movement);
    setHasNotDocument(data.has_not_document);
  }, [data]);

  const saveDocument = async (formData, form, shouldCloseDrawer = true) => {
    setIsSubmiting(true);
    try {
      let response;
      if (!data || !data.task_record_id) {

        formData.append(
          'task_record_data',
          JSON.stringify({
            task_id: task && task.id,
            customer_id: data && data.customer && data.customer.id,
            due_date: task && task.due_date,
          })
        );

        response = await request('post', '/calendar/task_record/0/fields', formData, false);
      } else {
        response = await request('post', `/calendar/task_record/${data.task_record_id}/fields`, formData, false);
      }

      if (shouldCloseDrawer) {
        // Reset form and close drawer
        form.resetFields();
        setFiles([]);
        closeDrawer(response.data.data);
      } else {
        // Keep drawer open, update data but don't reset form
        // Update files with the response data instead of clearing them
        setFiles([]); // Clear local files since they were saved
        // Update the data without closing the drawer
        onClose(response.data.data, false);
      }
    } catch (error) {
      console.error('Error in saveDocument:', error);
      console.error('Error details:', {
        data,
        task,
        formValues: form.getFieldsValue()
      });
    } finally {
      setIsSubmiting(false);
    }
  };

  const handleSubmit = () => {
    form.validateFields(async (err, values) => {
      const { file_upload } = task;
      const { files: filesFromData } = data;
      const allFiles = [...filesFromData, ...files];

      if (!hasNotDocument) {
        if ((file_upload === 1 && allFiles.length === 0) && !isWithoutMovement) {
          setHasRequiredDocumentError(true);
          return;
        }
      }

      if (err) {
        return;
      }

      if (hasRequiredDocumentError) {
        clearHasRequiredDocumentError();
      }

      const formData = new FormData();

      formData.append('is_without_movement', isWithoutMovement ? '1' : '0');
      formData.append('has_not_document', hasNotDocument ? '1' : '0');

      if (files.length > 0) {
        files.forEach((value) => {
          formData.append('files[]', value.file);

          const { file, ...onlyFilesInfo } = value;
          formData.append('files_info[]', JSON.stringify(onlyFilesInfo));
        });
      }

      Object.keys(values).forEach((key) => {
        formData.append(key, values[key]);
      });

      if (data && data.executed_at) {
        await saveDocument(formData, form);
      } else {
        Modal.confirm({
          title: 'Deseja marcar essa tarefa como executada?',
          content: 'Você poderá marcar como não executada depois.',
          okText: 'Sim',
          cancelText: 'Não',
          onOk: async () => {
            formData.append('mark_as_executed', 1);
            await saveDocument(formData, form);
          },
          onCancel: async () => {
            formData.append('mark_as_executed', 0);
            await saveDocument(formData, form, false);
            // Don't close the drawer - let the user continue editing
          },
        });
      }
    });
  };

  const getUserById = (id) => {
    return users && users.find((item) => item.id === id);
  };

  const getTooltipTitle = () => {
    if (!data || !data.is_without_movement || !isWithoutMovement) return '';

    const user = getUserById(data.checked_as_without_movement_by);
    const checkedAsWithoutMovementAt = moment(data.checked_as_without_movement_at).format('DD/MM/YYYY HH:mm');

    if (user) {
      return `Marcado as ${checkedAsWithoutMovementAt} por ${user.name}`;
    }

    return `Marcado as ${checkedAsWithoutMovementAt}`;
  };

  const closeDrawer = (responseData = null) => {
    if (hasRequiredDocumentError) {
      clearHasRequiredDocumentError();
    }

    setFiles([]);
    setDocumentDrawer({
      isOpen: false,
      data: null
    });
    onClose(responseData);
  };

  const clearHasRequiredDocumentError = () => {
    setHasRequiredDocumentError(false);
  };

  const clearFieldsErrors = () => {
    const currentFields = form.getFieldsValue();

    const fieldsWithoutErrors = {};

    Object.entries(currentFields).forEach(([key, value]) => {
      fieldsWithoutErrors[key] = {
        value,
        errors: null
      }
    });

    form.setFields(fieldsWithoutErrors);
  };

  const handleWithoutMovement = (isChecked) => {
    if (isChecked) {
      clearFieldsErrors();
      clearHasRequiredDocumentError();
    }

    setIsWithoutMovement(isChecked);
  };

  const handleHasNotDocument = (isChecked) => {
    if (isChecked) {
      clearHasRequiredDocumentError();
      const { setFieldsValue } = form;
      const customFields = task.custom_fields
        ? Object.entries(task.custom_fields)
        : [];

      customFields.forEach(([fieldKey, fieldValue]) => {
        const fieldType = fieldValue[1];

        if (fieldType === 'money') {
          setFieldsValue({
            [fieldKey]: '0,00'
          });
        }
      });
    }

    setHasNotDocument(isChecked);
  };

  const openDocumentDrawer = (data = null) => {
    setDocumentDrawer({
      isOpen: true,
      data
    });
  };

  const closeDocumentDrawer = () => {
    setDocumentDrawer({
      isOpen: false,
      data: null
    });
  };

  const onCreateDocument = (document) => {
    setFiles(prevFiles => [...prevFiles, document]);
    clearHasRequiredDocumentError();
  };

  const onUpdateDocument = (document) => {
    setFiles(prevFiles => 
      prevFiles.map((existingDocument) => 
        existingDocument.id !== document.id ? existingDocument : document
      )
    );
  };

  const { getFieldDecorator } = form;
  const filesList = data ? [...data.files, ...files] : [];

  return (
    <StyledDrawer
      title={data && data.customer.name}
      width={filesList.length > 0 ? 1200 : 320}
      onClose={closeDrawer}
      visible={data !== null}
      destroyOnClose={true}
      maskClosable={true}
      afterVisibleChange={(isVisible) => {
        if (!isVisible) {
          closeDrawer();
        }
      }}
    >
      {task && (
        <Form layout="vertical">
          <Row gutter={16}>
            {task.custom_fields &&
              Object.keys(task.custom_fields).map((key) => (
                <Col span={24} key={key}>
                  <Form.Item label={task.custom_fields[key][0]}>
                    {getFieldDecorator(key, {
                      initialValue:
                        data &&
                        data.custom_fields !== null &&
                        data.custom_fields.hasOwnProperty(key) &&
                        data.custom_fields[key] !== 'null'
                          ? data.custom_fields[key]
                          : '',
                      rules: !isWithoutMovement && !hasNotDocument ? [
                        {
                          message: 'Por favor, preencha o campo!',
                          required: true,
                        },
                      ] : [],
                    })(<Input />)}
                  </Form.Item>
                </Col>
              ))}
          </Row>
          {task.file_upload === 1 && (
            <DocumentButtonWrapper>
              <AddDocumentButton
                disabled={hasNotDocument}
                className={hasRequiredDocumentError ? 'has-error' : undefined}
                type="primary"
                onClick={() => openDocumentDrawer()}
              >
                Adicionar documento
              </AddDocumentButton>
              <RequiredDocumentErrorMessage
                className={hasRequiredDocumentError ? 'has-error' : undefined}
              >
                Por favor, adicione pelo menos um arquivo!
              </RequiredDocumentErrorMessage>
              <SwitchContainer>
                <SwtichStyled
                  size="small"
                  checked={hasNotDocument}
                  onChange={handleHasNotDocument}
                />
                <SwitchLabel>Não há documentos</SwitchLabel>
              </SwitchContainer>
              {filesList.length > 0 && (
                <DocumentsTable
                  files={filesList}
                  selectedTaskRecord={data}
                  openDocumentDrawer={openDocumentDrawer}
                />
              )}
            </DocumentButtonWrapper>
          )}
          <BottonsContainer>
            {data && data.files.length > 0 && (
              <ButtonsWrapper>
                <DeliveryMethodButtons
                  filesInState={files}
                  data={data}
                  updateCustomers={updateCustomers}
                />
              </ButtonsWrapper>
            )}
            <SwitchContainer>
              <Tooltip title={getTooltipTitle()}>
                <SwtichStyled
                  size="small"
                  checked={isWithoutMovement}
                  onChange={handleWithoutMovement}
                />
              </Tooltip>
              <SwitchLabel>Empresa sem movimentação</SwitchLabel>
            </SwitchContainer>
          </BottonsContainer>
        </Form>
      )}
      <Footer>
        <Button onClick={closeDrawer}>Voltar</Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={isSubmiting}
        >
          Salvar
        </Button>
      </Footer>
      <DocumentUpload
        visible={documentDrawer.isOpen}
        data={documentDrawer.data}
        onClose={closeDocumentDrawer}
        onCreateDocument={onCreateDocument}
        onUpdateDocument={onUpdateDocument}
        task={task}
      />
    </StyledDrawer>
  );
};

export default Form.create()(CustomFields);
